import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  TablePagination,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import EditIcon from '@mui/icons-material/Edit'
import HistoryIcon from '@mui/icons-material/History'
import DownloadIcon from '@mui/icons-material/Download'
import { upcomingPromotionService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import ManagementSearchBar from '../components/shared/ManagementSearchBar'
import TableLoadingRow from '../components/shared/TableLoadingRow'
import TableEmptyState from '../components/shared/TableEmptyState'
import useAuthStore from '../store/authStore'
import { canExportData } from '../utils/adminPermissions'

function toUtcYmd(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function formatDisplayDate(value) {
  return toUtcYmd(value) || '—'
}

const ManageUpcomingPromotionsPage = () => {
  const navigate = useNavigate()
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const showExport = canExportData(adminPermissions)

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  const [debouncedSearch, setDebouncedSearch] = useState('')

  const applySearchQuery = useCallback((q) => {
    setDebouncedSearch(q)
    setPage(0)
  }, [])

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [editFormData, setEditFormData] = useState({
    sku: '',
    description: '',
    start: '',
    end: '',
    value_adjustment: '',
  })
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [historySku, setHistorySku] = useState(null)
  const [historyItems, setHistoryItems] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [exportAnchor, setExportAnchor] = useState(null)
  const [exporting, setExporting] = useState(false)

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true)
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      }
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim()
      }

      const data = await upcomingPromotionService.list(params)
      if (data && data.items) {
        setItems(data.items)
        setTotalItems(data.pagination?.total || 0)
      } else {
        setItems([])
        setTotalItems(0)
      }
    } catch (err) {
      console.error('Error fetching upcoming promotions:', err)
      setItems([])
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, debouncedSearch])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  const handleChangePage = (_event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleHistoryClick = async (row) => {
    const sku = row?.sku
    if (!sku) return
    setHistorySku(sku)
    setHistoryItems([])
    setHistoryLoading(true)
    try {
      const data = await upcomingPromotionService.listEditLogs({
        sku,
        page: 1,
        limit: 50,
      })
      setHistoryItems(Array.isArray(data?.items) ? data.items : [])
    } catch (err) {
      console.error('Error fetching upcoming promotion edit logs:', err)
      setHistoryItems([])
    } finally {
      setHistoryLoading(false)
    }
  }

  const handleEditClick = (row) => {
    setSelectedRow(row)
    setEditFormData({
      sku: row.sku || '',
      description: row.description || '',
      start: toUtcYmd(row.start) || row.start || '',
      end: toUtcYmd(row.end) || row.end || '',
      value_adjustment:
        row.value_adjustment == null ? '' : String(row.value_adjustment),
    })
    setActionError(null)
    setEditDialogOpen(true)
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setSelectedRow(null)
    setEditFormData({
      sku: '',
      description: '',
      start: '',
      end: '',
      value_adjustment: '',
    })
    setActionError(null)
  }

  const handleEditChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleExport = async (format) => {
    setExportAnchor(null)
    if (exporting) return
    if (totalItems === 0) {
      setSuccessMessage(null)
      setActionError('No upcoming promotions found to export')
      return
    }
    try {
      setExporting(true)
      setActionError(null)
      const response = await upcomingPromotionService.export({
        format,
        search: debouncedSearch,
      })
      navigate('/pos-store-promotions/upcoming/exports', {
        state: {
          queuedMessage: response?.jobId
            ? `Export started (Job ID ${response.jobId}). Watching for progress…`
            : response?.message ||
              'Export started. Watching for progress…',
        },
      })
    } catch (err) {
      console.error('Error exporting upcoming promotions:', err)
      setActionError(
        getApiErrorMessage(err, 'Failed to start upcoming promotions export'),
      )
    } finally {
      setExporting(false)
    }
  }

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true)
      setActionError(null)

      if (!selectedRow?.id) {
        setActionError('Upcoming promotion ID is required for update')
        return
      }

      const sku = String(editFormData.sku || '').trim()
      const start = String(editFormData.start || '').trim()
      const end = String(editFormData.end || '').trim()
      const description = String(editFormData.description || '').trim()

      if (!sku) {
        setActionError('Article Code is required')
        return
      }
      if (!start || !end) {
        setActionError('Start and end dates are required')
        return
      }
      if (start > end) {
        setActionError('Start date must be on or before end date')
        return
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
        setActionError('Start and end must be YYYY-MM-DD')
        return
      }
      const adjustment = String(editFormData.value_adjustment ?? '').trim()
      if (!/^\d+(\.\d{1,2})?$/.test(adjustment)) {
        setActionError(
          'Vendor amount must be 0 or greater with at most 2 decimal places',
        )
        return
      }

      await upcomingPromotionService.update(selectedRow.id, {
        sku,
        start,
        end,
        description: description || null,
        value_adjustment: adjustment,
      })
      setSuccessMessage('Upcoming promotion updated successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
      handleEditClose()
      fetchItems()
    } catch (err) {
      console.error('Error updating upcoming promotion:', err)
      const message = err.response?.data?.message
      setActionError(
        Array.isArray(message)
          ? message.join(', ')
          : message || 'Failed to update upcoming promotion',
      )
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 1 }}
          >
            Manage Upcoming Promotions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and maintain upcoming promotion advertising entries
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {showExport ? (
            <>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() =>
                  navigate('/pos-store-promotions/upcoming/exports')
                }
                aria-label="View upcoming promotion exports"
              >
                View exports
              </Button>
              <Button
                variant="outlined"
                startIcon={
                  exporting ? <CircularProgress size={16} /> : <DownloadIcon />
                }
                onClick={(event) => setExportAnchor(event.currentTarget)}
                disabled={exporting || loading}
                aria-label="Export upcoming promotions"
              >
                Export
              </Button>
              <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
              >
                <MenuItem
                  onClick={() => handleExport('csv')}
                  disabled={exporting}
                >
                  CSV
                </MenuItem>
                <MenuItem
                  onClick={() => handleExport('xlsx')}
                  disabled={exporting}
                >
                  Excel (.xlsx)
                </MenuItem>
              </Menu>
            </>
          ) : null}
          <Tooltip title="Refresh">
            <IconButton onClick={fetchItems} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {actionError && !editDialogOpen ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      ) : null}

      <ManagementSearchBar
        label="Search by Article Code, Product Name, or Description"
        placeholder="Enter Article Code, product name, or description..."
        onApplySearchQuery={applySearchQuery}
      />

      <Card
        sx={{
          borderRadius: 2,
          boxShadow:
            '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Article Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Product Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Vendor ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Vendor amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sheet version</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Uploaded</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableLoadingRow colSpan={11} />
                ) : items.length === 0 ? (
                  <TableEmptyState
                    colSpan={11}
                    message="No upcoming promotions found. Upload a CSV to get started."
                  />
                ) : (
                  items.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            bgcolor: '#f5f5f5',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            display: 'inline-block',
                          }}
                        >
                          {row.sku}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {row.product_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.description || <em>None</em>}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.vendor_id != null ? (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {String(row.vendor_id).padStart(6, '0')}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{formatDisplayDate(row.start)}</TableCell>
                      <TableCell>{formatDisplayDate(row.end)}</TableCell>
                      <TableCell align="right">
                        {row.value_adjustment == null
                          ? '—'
                          : `৳${Number(row.value_adjustment).toFixed(2)}`}
                      </TableCell>
                      <TableCell>{row.status || 'active'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.upload_file_name || '—'}
                        </Typography>
                        {row.upload_log_id ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {String(row.upload_log_id).slice(0, 8)}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.uploaded_by_email || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.uploaded_at
                            ? new Date(row.uploaded_at).toLocaleString()
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit history">
                          <IconButton
                            size="small"
                            onClick={() => handleHistoryClick(row)}
                            sx={{ color: '#475569' }}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {row.status === 'superseded' ? null : (
                          <Tooltip title="Save as a new version">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(row)}
                              sx={{ color: '#1976d2' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
          Edit Upcoming Promotion
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setActionError(null)}
            >
              {actionError}
            </Alert>
          )}
          <TextField
            label="Article Code"
            fullWidth
            margin="normal"
            value={editFormData.sku}
            onChange={(e) => handleEditChange('sku', e.target.value)}
            required
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={editFormData.description}
            onChange={(e) => handleEditChange('description', e.target.value)}
            multiline
            minRows={2}
          />
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            margin="normal"
            value={editFormData.start}
            onChange={(e) => handleEditChange('start', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="End Date"
            type="date"
            fullWidth
            margin="normal"
            value={editFormData.end}
            onChange={(e) => handleEditChange('end', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Vendor amount (BDT per unit)"
            fullWidth
            margin="normal"
            value={editFormData.value_adjustment}
            onChange={(e) => handleEditChange('value_adjustment', e.target.value)}
            required
            helperText="Only the amount the vendor will reimburse"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleEditClose} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={actionLoading}
            sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#cc0000' } }}
            startIcon={
              actionLoading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {actionLoading ? 'Saving...' : 'Save new version'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(historySku)}
        onClose={() => setHistorySku(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
          Edit history {historySku ? `· ${historySku}` : ''}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ my: 2 }}>
            Invoices keep the vendor amount that was active when they were sold.
            A later change to 0 does not rewrite those disbursements.
          </Alert>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : historyItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No vendor amount edits recorded for this article yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Who</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Previous amount</TableCell>
                  <TableCell align="right">New amount</TableCell>
                  <TableCell>Previous window</TableCell>
                  <TableCell>New window</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyItems.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {log.created_at
                        ? new Date(log.created_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell>{log.admin_email || '—'}</TableCell>
                    <TableCell>
                      {log.source === 'csv_upload' ? 'CSV upload' : 'Manual edit'}
                    </TableCell>
                    <TableCell align="right">
                      {log.previous_value_adjustment == null
                        ? '—'
                        : `৳${Number(log.previous_value_adjustment).toFixed(2)}`}
                    </TableCell>
                    <TableCell align="right">
                      {log.new_value_adjustment == null
                        ? '—'
                        : `৳${Number(log.new_value_adjustment).toFixed(2)}`}
                    </TableCell>
                    <TableCell>
                      {log.previous_start || '—'} – {log.previous_end || '—'}
                    </TableCell>
                    <TableCell>
                      {log.new_start || '—'} – {log.new_end || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistorySku(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  )
}

export default ManageUpcomingPromotionsPage
